import React, { useState, useEffect, useCallback } from "react";
import Button from "@mui/material/Button";
import SearchInput from "../Constants/search_input";
import {
  Download,
  Edit,
  Share2,
  FileText,
  TrendingUp,
  Clock,
  SquareCheck,
  AlertCircle,
} from "lucide-react";
import jsPDF from "jspdf";
import Modal from "@mui/material/Modal";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {
  fontFamily,
  paginationstyle,
  screenHeaderStyle,
  useSharedValue,
} from "../Constants/constants";
import { getUserDetails } from "../../services/userApiService";
import { formatDate } from "../../Utility/utility";
import LoaderIndicator from "../../Utility/loading";
import { ConditionalPagination } from "../../utils/paginationUtils";
import {
  formatAmount,
  formatAmountWithCurrency,
  calculateAmount,
  DEFAULT_EXTRACTION_PRICE,
  DEFAULT_CLASSIFICATION_PRICE,
  DEFAULT_CURRENCY,
  CURRENCY_OPTIONS,
  getAllInvoices,
  getInvoicesByOrganization,
  getInvoiceSummary,
  getPageUsageByOrganization,
  generateInvoice,
  updateInvoice,
  deleteInvoice,
  InvoiceData,
  InvoiceSummary,
  PageUsageData,
  CurrencyOption,
} from "./InvoiceService";
import { FormDate } from "../../Forms/common";
import "./Invoice.css";

// Convert MM/DD/YYYY → YYYY-MM-DD for API calls
const toISODate = (mmddyyyy: string): string => {
  if (!mmddyyyy) return "";
  const clean = mmddyyyy.trim();
  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) return clean;
  // MM/DD/YYYY → YYYY-MM-DD
  const parts = clean.split("/");
  if (parts.length === 3) {
    const [month, day, year] = parts;
    return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  }
  return clean;
};

// Convert YYYY-MM-DD → MM/DD/YYYY for FormDate display
const toDisplayDate = (isoDate: string): string => {
  if (!isoDate) return "";
  const clean = isoDate.trim().split("T")[0]; // strip time part
  // Already MM/DD/YYYY
  if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(clean)) return clean;
  // YYYY-MM-DD → MM/DD/YYYY
  if (/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    const [year, month, day] = clean.split("-");
    return `${month}/${day}/${year}`;
  }
  return clean;
};

const columns = [
  { field: "invoiceNumber", headerName: "Invoice #" },
  { field: "organizationName", headerName: "Organization" },
  { field: "invoiceDate", headerName: "Invoice Date" },
  { field: "periodStart", headerName: "Period Start" },
  { field: "periodEnd", headerName: "Period End" },
  { field: "totalPages", headerName: "Total Pages" },
  { field: "totalAmount", headerName: "Amount ($)" },
  { field: "status", headerName: "Status" },
  { field: "functions", headerName: "" },
];

export default function Invoice() {
  // ============================================================
  //  STATE
  // ============================================================
  const [searchValue, setSearchValue] = useState("");

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<InvoiceData[]>([]);
  const [summary, setSummary] = useState<InvoiceSummary | null>(null);
  const [pageUsage, setPageUsage] = useState<PageUsageData | null>(null);

  // Role detection — SuperAdmin gets full CRUD, org Admin gets read-only view
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  useEffect(() => {
    const checkSuperAdmin = async () => {
      try {
        const user = await getUserDetails();
        setIsSuperAdmin(user?.isSuperAdmin === true);
      } catch {
        setIsSuperAdmin(false);
      }
    };
    checkSuperAdmin();
  }, []);

  // Modals
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // Generate modal
  const [genStartDate, setGenStartDate] = useState("");
  const [genEndDate, setGenEndDate] = useState("");
  const [genEstPages, setGenEstPages] = useState(0);
  const [genEstAmount, setGenEstAmount] = useState(0);
  const [generating, setGenerating] = useState(false);
  const [currency, setCurrency] = useState<CurrencyOption>(DEFAULT_CURRENCY);
  const [extractionPrice, setExtractionPrice] = useState<number>(DEFAULT_EXTRACTION_PRICE); // in full currency units (e.g. £0.10)
  const [classificationPrice, setClassificationPrice] = useState<number>(
    DEFAULT_CLASSIFICATION_PRICE
  );

  // Edit modal
  const [editingInvoice, setEditingInvoice] = useState<InvoiceData | null>(null);
  const [editFields, setEditFields] = useState<Record<string, any>>({});
  const [emailIds, setEmailIds] = useState("");

  // Delete modal
  const [selectedInvoiceId, setSelectedInvoiceId] = useState<string | null>(null);
  const [deleteReason, setDeleteReason] = useState("");

  // Pagination
  const [currentPage, setCurrtPage] = useState(1);
  const [rowsPerPage] = useState(10);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  // Date filters
  const [dateFilterStart, setDateFilterStart] = useState("");
  const [dateFilterEnd, setDateFilterEnd] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  // Snackbar
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "info",
  });

  const { defaultOrganization, isMenuOpen } = useSharedValue();

  // ============================================================
  //  DATA LOADING
  // ============================================================
  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      let invoices: InvoiceData[];

      if (defaultOrganization?.id) {
        invoices = await getInvoicesByOrganization(defaultOrganization.id);
      } else {
        invoices = await getAllInvoices(
          toISODate(dateFilterStart) || undefined,
          toISODate(dateFilterEnd) || undefined,
          statusFilter || undefined
        );
      }
      setRows(invoices);
    } catch (err: any) {
      console.error("Error loading invoices:", err);
      showSnackbar("Failed to load invoices. Please try again.", "error");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [defaultOrganization, dateFilterStart, dateFilterEnd, statusFilter]);

  const loadSummary = useCallback(async () => {
    try {
      const data = await getInvoiceSummary();
      setSummary(data);
    } catch (err) {
      console.error("Error loading summary:", err);
    }
  }, []);

  const loadPageUsage = useCallback(async () => {
    if (!defaultOrganization?.id) return;
    try {
      const data = await getPageUsageByOrganization(defaultOrganization.id);
      setPageUsage(data);
    } catch (err) {
      console.error("Error loading page usage:", err);
    }
  }, [defaultOrganization]);

  useEffect(() => {
    loadInvoices();
    loadSummary();
    loadPageUsage();
  }, [loadInvoices, loadSummary, loadPageUsage]);

  // ============================================================
  //  HELPERS
  // ============================================================
  const showSnackbar = (message: string, severity: "success" | "error" | "info") => {
    setSnackbar({ open: true, message, severity });
  };

  const handlePageChange = (_: any, page: number) => {
    setCurrtPage(page);
  };

  // ============================================================
  //  GENERATE INVOICE
  // ============================================================
  const handleOpenGenerate = async () => {
    setGenStartDate("");
    setGenEndDate("");
    setGenEstPages(0);
    setGenEstAmount(0);
    setCurrency(DEFAULT_CURRENCY);
    setExtractionPrice(DEFAULT_EXTRACTION_PRICE);
    setClassificationPrice(DEFAULT_CLASSIFICATION_PRICE);
    setIsGenerateModalOpen(true);
  };

  // When dates change in generate modal, fetch unbilled page count
  useEffect(() => {
    if (!genStartDate || !genEndDate || !defaultOrganization?.id) {
      setGenEstPages(0);
      setGenEstAmount(0);
      return;
    }
    const fetchEstimate = async () => {
      try {
        const usage = await getPageUsageByOrganization(
          defaultOrganization.id,
          toISODate(genStartDate),
          toISODate(genEndDate)
        );
        setGenEstPages(usage.unbilledPages);
        setGenEstAmount(usage.unbilledAmount);
      } catch {
        setGenEstPages(0);
        setGenEstAmount(0);
      }
    };
    fetchEstimate();
  }, [genStartDate, genEndDate, defaultOrganization]);

  const handleGenerateInvoice = async () => {
    if (!defaultOrganization?.id) {
      showSnackbar("Please select an organization first.", "error");
      return;
    }
    if (!genStartDate || !genEndDate) {
      showSnackbar("Please select both start and end dates.", "error");
      return;
    }
    if (new Date(toISODate(genStartDate)) > new Date(toISODate(genEndDate))) {
      showSnackbar("Start date cannot be after end date.", "error");
      return;
    }
    if (genEstPages <= 0) {
      showSnackbar("No unbilled extraction pages found for the selected period.", "error");
      return;
    }

    try {
      setGenerating(true);
      const invoice = await generateInvoice({
        organizationId: defaultOrganization.id,
        startDate: toISODate(genStartDate),
        endDate: toISODate(genEndDate),
        pricePerExtractionPage: Math.round(extractionPrice * 100), // convert to pence for API
        pricePerClassificationPage: Math.round(classificationPrice * 100),
      });
      showSnackbar(
        `Invoice ${invoice.invoiceNumber} generated successfully! Amount: ${formatAmount(invoice.totalAmount)}`,
        "success"
      );
      setIsGenerateModalOpen(false);
      loadInvoices();
      loadSummary();
      loadPageUsage();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to generate invoice.", "error");
    } finally {
      setGenerating(false);
    }
  };

  // ============================================================
  //  DOWNLOAD PDF (client-side generation — Professional layout)
  // ============================================================
  const handleDownloadPDF = async (invoice: InvoiceData) => {
    const formatDateOnly = (value: string | null | undefined): string => {
      if (!value) return "-";
      const d = new Date(value);
      if (isNaN(d.getTime())) return "-";
      return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
    };
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();

      // ---- Load logo ----
      let logoLoaded = false;
      try {
        const img = new Image();
        img.crossOrigin = "anonymous";
        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            logoLoaded = true;
            resolve();
          };
          img.onerror = () => resolve(); // continue without logo
          img.src = "/CE.png";
        });
        if (logoLoaded) {
          doc.addImage(img, "PNG", 15, 10, 20, 20);
        }
      } catch {
        /* continue without logo */
      }

      // ---- Company Header ----
      const headerX = logoLoaded ? 40 : 15;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(20);
      doc.setTextColor(26, 26, 46); // #1a1a2e
      doc.text("Comply Extractor", headerX, 20);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(128, 134, 139);
      doc.text("Automated Document Processing & Extraction", headerX, 27);

      // ---- INVOICE badge (right) ----
      doc.setFont("helvetica", "bold");
      doc.setFontSize(26);
      doc.setTextColor(42, 167, 224);
      doc.text("INVOICE", pageWidth - 15, 22, { align: "right" });

      // ---- Accent bar ----
      doc.setFillColor(42, 167, 224);
      doc.rect(15, 34, pageWidth - 30, 2, "F");

      // ---- Invoice metadata (left + right columns) ----
      let y = 46;
      doc.setFontSize(10);
      const labelColor = () => {
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
      };
      const valueColor = () => {
        doc.setFont("helvetica", "normal");
        doc.setTextColor(30, 30, 30);
      };

      const lineHeight = 5;
      const rowGap = 3;

      type MetaFieldOptions = {
        label: string;
        value: string;
        labelX: number;
        valueX: number;
        valueMaxWidth: number;
        isStatus?: boolean;
      };

      const drawMetaField = ({
        label,
        value,
        labelX,
        valueX,
        valueMaxWidth,
        isStatus = false,
      }: MetaFieldOptions): number => {
        labelColor();
        doc.text(label, labelX, y);

        const safeValue = value || "-";
        const lines = doc.splitTextToSize(safeValue, valueMaxWidth) as string[];

        if (isStatus) {
          const statusValue = safeValue.toLowerCase();
          if (statusValue === "paid") doc.setTextColor(16, 185, 129);
          else if (statusValue === "overdue") doc.setTextColor(239, 68, 68);
          else if (statusValue === "pending") doc.setTextColor(245, 158, 11);
          else doc.setTextColor(100, 100, 100);
          doc.setFont("helvetica", "bold");
          doc.text(
            lines.map((line) => line.toUpperCase()),
            valueX,
            y
          );
        } else {
          valueColor();
          doc.text(lines, valueX, y);
        }

        return Math.max(1, lines.length);
      };

      const metadataRows = [
        {
          left: { label: "Invoice Number:", value: String(invoice.invoiceNumber || "-") },
          right: { label: "Organization:", value: String(invoice.organizationName || "-") },
        },
        {
          left: { label: "Invoice Date:", value: formatDateOnly(invoice.invoiceDate) },
          right: { label: "Status:", value: String(invoice.status || "-") },
        },
        {
          left: {
            label: "Billing Period:",
            value: `${formatDateOnly(invoice.periodStart)} — ${formatDateOnly(invoice.periodEnd)}`,
          },
          right: {
            label: "Due Date:",
            value: invoice.dueDate ? formatDateOnly(invoice.dueDate) : "-",
          },
        },
      ];

      metadataRows.forEach((row) => {
        const leftLines = drawMetaField({
          label: row.left.label,
          value: row.left.value,
          labelX: 15,
          valueX: 60,
          valueMaxWidth: 54,
        });

        const rightLines = drawMetaField({
          label: row.right.label,
          value: row.right.value,
          labelX: 120,
          valueX: 155,
          valueMaxWidth: 36,
          isStatus: row.right.label === "Status:",
        });

        y += Math.max(leftLines, rightLines) * lineHeight + rowGap;
      });

      // ---- Line items table ----
      const tableTop = y + 6;

      // Table header
      doc.setFillColor(42, 167, 224);
      doc.rect(15, tableTop, pageWidth - 30, 12, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(255, 255, 255);
      doc.text("DESCRIPTION", 20, tableTop + 8);
      doc.text("PAGES", 105, tableTop + 8);
      doc.text("RATE (p/pg)", 130, tableTop + 8);
      doc.text("AMOUNT (£)", pageWidth - 20, tableTop + 8, { align: "right" });

      // Extraction row
      const row1Y = tableTop + 12;
      doc.setFillColor(248, 249, 250);
      doc.rect(15, row1Y, pageWidth - 30, 11, "F");
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(30, 30, 30);
      doc.text("Document Extraction Processing", 20, row1Y + 7.5);
      doc.text(invoice.totalPages.toLocaleString(), 105, row1Y + 7.5);
      doc.text(`${currency.symbol}${extractionPrice.toFixed(2)}`, 130, row1Y + 7.5);
      const extractionAmount = invoice.totalPages * extractionPrice;
      doc.text(
        formatAmountWithCurrency(extractionAmount, currency.symbol),
        pageWidth - 20,
        row1Y + 7.5,
        { align: "right" }
      );

      // Classification row (if classification price > 0)
      const row2Y = row1Y + 11;
      doc.setFillColor(255, 255, 255);
      doc.rect(15, row2Y, pageWidth - 30, 11, "F");
      doc.text("Document Classification Processing", 20, row2Y + 7.5);
      doc.text("—", 105, row2Y + 7.5);
      doc.text(`${currency.symbol}${classificationPrice.toFixed(2)}`, 130, row2Y + 7.5);
      doc.text(
        classificationPrice > 0 ? formatAmountWithCurrency(0, currency.symbol) : "Free",
        pageWidth - 20,
        row2Y + 7.5,
        { align: "right" }
      );

      // Table border
      doc.setDrawColor(224, 224, 224);
      doc.rect(15, tableTop, pageWidth - 30, 34);
      doc.line(15, tableTop + 12, pageWidth - 15, tableTop + 12);
      doc.line(15, row1Y + 11, pageWidth - 15, row1Y + 11);

      // ---- Total box ----
      const totalY = row2Y + 20;
      doc.setFillColor(42, 167, 224);
      doc.roundedRect(pageWidth - 85, totalY, 70, 16, 3, 3, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(13);
      doc.setTextColor(255, 255, 255);
      doc.text(
        `Total: ${formatAmountWithCurrency(invoice.totalAmount, currency.symbol)}`,
        pageWidth - 50,
        totalY + 11,
        { align: "center" }
      );

      // ---- Payment info ----
      const infoY = totalY + 28;
      doc.setFillColor(248, 249, 250);
      doc.roundedRect(15, infoY, pageWidth - 30, 24, 3, 3, "F");
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(100, 100, 100);
      doc.text("Payment Terms", 20, infoY + 8);
      doc.setFont("helvetica", "normal");
      doc.setTextColor(60, 60, 60);
      doc.text(
        "Payment is due within 30 days of the invoice date. Please reference the invoice",
        20,
        infoY + 15
      );
      doc.text("number when making payment.", 20, infoY + 21);

      // ---- Notes (if any) ----
      if (invoice.notes) {
        const notesY = infoY + 32;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.setTextColor(100, 100, 100);
        doc.text("Notes:", 15, notesY);
        doc.setFont("helvetica", "normal");
        doc.setTextColor(60, 60, 60);
        doc.text(invoice.notes, 15, notesY + 7, { maxWidth: pageWidth - 30 });
      }

      // ---- Footer ----
      const footerY = 275;
      doc.setDrawColor(224, 224, 224);
      doc.line(15, footerY, pageWidth - 15, footerY);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(160, 160, 160);
      doc.text(
        "Generated by Comply Extractor — Automated Document Processing Platform",
        pageWidth / 2,
        footerY + 6,
        { align: "center" }
      );
      doc.text(
        `Invoice ${invoice.invoiceNumber} · ${formatDateOnly(invoice.invoiceDate)}`,
        pageWidth / 2,
        footerY + 12,
        { align: "center" }
      );

      doc.save(`${invoice.invoiceNumber}.pdf`);
      showSnackbar(`Invoice ${invoice.invoiceNumber} downloaded.`, "success");
    } catch (err) {
      console.error("Error generating PDF:", err);
      showSnackbar("Failed to generate PDF.", "error");
    }
  };

  // ============================================================
  //  EDIT INVOICE
  // ============================================================
  const handleEditInvoice = (invoice: InvoiceData) => {
    setEditingInvoice(invoice);
    setEditFields({
      invoiceNumber: invoice.invoiceNumber,
      periodStart: invoice.periodStart?.split("T")[0] || "",
      periodEnd: invoice.periodEnd?.split("T")[0] || "",
      totalPages: invoice.totalPages,
      totalAmount: invoice.totalAmount,
      status: invoice.status,
      notes: invoice.notes || "",
    });
    setEmailIds("");
    setIsEditModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editingInvoice) return;
    try {
      setLoading(true);
      await updateInvoice(editingInvoice.invoiceId, editFields);
      showSnackbar("Invoice updated successfully.", "success");
      setIsEditModalOpen(false);
      setEditingInvoice(null);
      loadInvoices();
      loadSummary();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to update invoice.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  //  SEND INVOICE (placeholder)
  // ============================================================
  const handleSendInvoice = (invoice: InvoiceData) => {
    showSnackbar(`Email sending for ${invoice.invoiceNumber} will be available soon.`, "info");
  };

  // ============================================================
  //  DELETE INVOICE
  // ============================================================
  const handleDeleteInvoice = async () => {
    if (!selectedInvoiceId || !deleteReason.trim()) return;
    try {
      setLoading(true);
      await deleteInvoice(selectedInvoiceId, deleteReason);
      showSnackbar("Invoice deleted successfully.", "success");
      setIsDeleteModalOpen(false);
      setSelectedInvoiceId(null);
      setDeleteReason("");
      loadInvoices();
      loadSummary();
    } catch (err: any) {
      showSnackbar(err.message || "Failed to delete invoice.", "error");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  //  FILTERING
  // ============================================================
  const filteredRows = rows.filter((row) => {
    const matchesSearch =
      !searchValue ||
      row.invoiceNumber?.toLowerCase().includes(searchValue.toLowerCase()) ||
      row.organizationName?.toLowerCase().includes(searchValue.toLowerCase()) ||
      row.status?.toLowerCase().includes(searchValue.toLowerCase());

    const matchesDateStart =
      !dateFilterStart || new Date(row.invoiceDate) >= new Date(toISODate(dateFilterStart));
    const matchesDateEnd =
      !dateFilterEnd ||
      new Date(row.invoiceDate) <= new Date(toISODate(dateFilterEnd) + "T23:59:59");
    const matchesStatus = !statusFilter || row.status === statusFilter;

    return matchesSearch && matchesDateStart && matchesDateEnd && matchesStatus;
  });

  // ============================================================
  //  RENDER
  // ============================================================
  return (
    <>
      <div className={`invoice-container ${screenHeaderStyle(isMenuOpen)}`}>
        {/* ===== HEADER ===== */}
        <div className="invoice-header">
          <div className="invoice-header-actions">
            {isSuperAdmin && (
              <Button
                disableElevation
                variant="contained"
                className="review-btn-filled"
                onClick={handleOpenGenerate}
              >
                + Generate Invoice
              </Button>
            )}
          </div>
        </div>
        {/* ===== SUMMARY CARDS ===== */}
        <div className="invoice-summary-cards">
          <div className="summary-card">
            <div className="summary-card-icon invoices-icon">
              <FileText size={18} />
            </div>
            <div className="summary-card-content">
              <div className="summary-card-value">{summary?.totalInvoices ?? 0}</div>
              <div className="summary-card-label">Total Invoices</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-icon revenue-icon">
              <TrendingUp size={18} />
            </div>
            <div className="summary-card-content">
              <div className="summary-card-value">{formatAmount(summary?.totalRevenue ?? 0)}</div>
              <div className="summary-card-label">Total Revenue</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-icon pending-icon">
              <Clock size={18} />
            </div>
            <div className="summary-card-content">
              <div className="summary-card-value">{formatAmount(summary?.pendingRevenue ?? 0)}</div>
              <div className="summary-card-label">Pending</div>
            </div>
          </div>

          <div className="summary-card">
            <div className="summary-card-icon unbilled-icon">
              <SquareCheck size={18} />
            </div>
            <div className="summary-card-content">
              <div className="summary-card-value">
                {pageUsage?.unbilledPages?.toLocaleString() ??
                  summary?.unbilledPages?.toLocaleString() ??
                  0}{" "}
                <span style={{ fontSize: "16px", fontWeight: 400 }}>pages</span>
              </div>
              <div className="summary-card-label">
                Unbilled ({formatAmount(pageUsage?.unbilledAmount ?? summary?.unbilledAmount ?? 0)})
              </div>
            </div>
          </div>
        </div>
        {/* ===== CLASSIFICATION & EXTRACTION BREAKDOWN ===== */}
        {summary && (summary.classification || summary.extraction) ? (
          <div className="process-breakdown-section">
            <div className="process-breakdown-header">
              <span className="process-breakdown-title">Usage Breakdown</span>
              <span className="process-breakdown-subtitle">
                {summary.packCount ?? 0} packs &middot; {summary.individualCount ?? 0} individual
                uploads
              </span>
            </div>

            <div className="process-breakdown-panels">
              {/* Classification Panel */}
              <div className="process-panel classification-panel">
                <div className="process-panel-header">
                  <span className="process-panel-icon classification-icon">C</span>
                  <div className="process-panel-title-wrap">
                    <span className="process-panel-title">Classification</span>
                    <span className="process-panel-pages">
                      {(summary.classification?.totalPages ?? 0).toLocaleString()} total pages
                    </span>
                  </div>
                  <div className="process-panel-stats">
                    <span className="panel-stat billed">
                      {(summary.classification?.billedPages ?? 0).toLocaleString()} billed
                    </span>
                    <span className="panel-stat unbilled">
                      {(summary.classification?.unbilledPages ?? 0).toLocaleString()} unbilled
                    </span>
                  </div>
                </div>
              </div>

              {/* Extraction Panel */}
              <div className="process-panel extraction-panel">
                <div className="process-panel-header">
                  <span className="process-panel-icon extraction-icon">E</span>
                  <div className="process-panel-title-wrap">
                    <span className="process-panel-title">Extraction</span>
                    <span className="process-panel-pages">
                      {(summary.extraction?.totalPages ?? 0).toLocaleString()} total pages
                    </span>
                  </div>
                  <div className="process-panel-stats">
                    <span className="panel-stat billed">
                      {(summary.extraction?.billedPages ?? 0).toLocaleString()} billed
                    </span>
                    <span className="panel-stat unbilled">
                      {(summary.extraction?.unbilledPages ?? 0).toLocaleString()} unbilled
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ) : null}
        {/* ===== TOOLBAR + TABLE CARD ===== */}
        <div className="invoice-table-card">
          {/* ===== TOOLBAR ===== */}
          <div className="invoice-toolbar">
            <div className="search-and-filters">
              <SearchInput
                value={searchValue}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setSearchValue(e.target.value)
                }
                onClear={() => setSearchValue("")}
                placeholder="Search invoices…"
                onFocus={undefined}
                onBlur={undefined}
                onClick={undefined}
                endAdornment={undefined}
              />

              <div className="filter-group">
                <div className="filter-date-wrap">
                  <FormDate
                    label=""
                    name="dateFilterStart"
                    value={dateFilterStart}
                    onChange={(e: any) => setDateFilterStart(e.target.value)}
                  />
                </div>
                <span className="filter-separator">—</span>
                <div className="filter-date-wrap">
                  <FormDate
                    label=""
                    name="dateFilterEnd"
                    value={dateFilterEnd}
                    onChange={(e: any) => setDateFilterEnd(e.target.value)}
                  />
                </div>
                <select
                  className="filter-status-select"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <option value="">All Status</option>
                  <option value="paid">Paid</option>
                  <option value="pending">Pending</option>
                  <option value="overdue">Overdue</option>
                  <option value="draft">Draft</option>
                  <option value="cancelled">Cancelled</option>
                </select>
                {(dateFilterStart || dateFilterEnd || statusFilter) && (
                  <Button
                    size="small"
                    className="clear-filters-btn"
                    onClick={() => {
                      setDateFilterStart("");
                      setDateFilterEnd("");
                      setStatusFilter("");
                    }}
                  >
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div className="toolbar-info">
              <span className="result-count">
                {filteredRows.length} invoice{filteredRows.length !== 1 ? "s" : ""}
              </span>
            </div>
          </div>

          {/* ===== TABLE ===== */}
          <div className="invoice-table-container">
            {/* Table Header */}
            <div className="table-header">
              {columns.map((col) => (
                <div
                  key={col.field}
                  className="table-header-cell"
                  style={{
                    fontFamily,
                    textAlign:
                      col.field === "totalPages" || col.field === "totalAmount"
                        ? "center"
                        : undefined,
                  }}
                >
                  {col.headerName}
                </div>
              ))}
            </div>

            {/* Table Body */}
            <div className="table-body">
              {loading ? (
                <div className="loading-state">
                  <LoaderIndicator />
                </div>
              ) : filteredRows.length === 0 ? (
                <div className="empty-state">
                  <FileText size={48} color="#80868b" strokeWidth={1.5} />
                  <div className="empty-state-title">No invoices found</div>
                  <div className="empty-state-subtitle">
                    {rows.length === 0
                      ? "Invoices will appear here after extraction page usage is recorded and billed."
                      : "Try adjusting your search or filters."}
                  </div>
                </div>
              ) : (
                <div>
                  {filteredRows.slice(startIndex, endIndex).map((row, index) => (
                    <div
                      key={row.invoiceId}
                      className={`table-row ${index % 2 === 0 ? "even" : "odd"}`}
                    >
                      {columns.map((column) => (
                        <div
                          key={column.field}
                          title={
                            column.field === "totalAmount"
                              ? formatAmount(row.totalAmount)
                              : String((row as any)[column.field] || "")
                          }
                          className={`table-cell ${column.field === "invoiceNumber" ? "invoice-number" : ""}`}
                          style={{ fontFamily }}
                        >
                          {column.field === "functions" ? (
                            <div className="functions-cell">
                              <div
                                onClick={() => handleDownloadPDF(row)}
                                className="action-button download-button"
                                title="Download PDF"
                              >
                                <Download size={16} />
                              </div>
                              {isSuperAdmin && (
                                <div
                                  onClick={() => handleEditInvoice(row)}
                                  className="action-button edit-button"
                                  title="Edit Invoice"
                                >
                                  <Edit size={16} />
                                </div>
                              )}
                              {isSuperAdmin && (
                                <div
                                  onClick={() => handleSendInvoice(row)}
                                  className="action-button send-button"
                                  title="Send Invoice"
                                >
                                  <Share2 size={16} />
                                </div>
                              )}
                            </div>
                          ) : column.field === "status" ? (
                            <span className={`status-badge ${row.status}`}>{row.status}</span>
                          ) : column.field === "invoiceDate" ||
                            column.field === "periodStart" ||
                            column.field === "periodEnd" ? (
                            formatDate((row as any)[column.field])
                          ) : column.field === "totalAmount" ? (
                            <span
                              className="amount-value"
                              style={{ display: "block", textAlign: "center" }}
                            >
                              {formatAmount(row.totalAmount)}
                            </span>
                          ) : column.field === "totalPages" ? (
                            <span
                              className="pages-value"
                              style={{ display: "block", textAlign: "center" }}
                            >
                              {row.totalPages.toLocaleString()}
                            </span>
                          ) : (
                            String((row as any)[column.field] || "")
                          )}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>{" "}
        {/* end invoice-table-card */}
        {/* ===== GENERATE INVOICE MODAL ===== */}
        <Modal open={isGenerateModalOpen} onClose={() => setIsGenerateModalOpen(false)}>
          <div className="modal-container">
            <h6 className="modal-title" style={{ fontFamily }}>
              GENERATE INVOICE
            </h6>
            <div className="modal-divider"></div>

            {defaultOrganization && (
              <div className="modal-organization" style={{ fontFamily }}>
                <strong>Organization:</strong> {defaultOrganization.name}
              </div>
            )}

            <div className="modal-date-inputs">
              <FormDate
                label="Billing Period Start"
                name="genStartDate"
                value={genStartDate}
                onChange={(e: any) => setGenStartDate(e.target.value)}
              />
              <FormDate
                label="Billing Period End"
                name="genEndDate"
                value={genEndDate}
                onChange={(e: any) => setGenEndDate(e.target.value)}
              />
            </div>

            {genEstPages > 0 && (
              <div className="modal-estimate-box" style={{ fontFamily }}>
                <div className="modal-estimate-row">
                  <span>Unbilled Pages:</span>
                  <strong>{genEstPages.toLocaleString()}</strong>
                </div>

                {/* Currency + Pricing */}
                <div className="modal-pricing-inputs">
                  <div className="pricing-input-row">
                    <label className="pricing-label">Currency:</label>
                    <select
                      className="currency-select"
                      value={currency.code}
                      onChange={(e) => {
                        const sel = CURRENCY_OPTIONS.find((c) => c.code === e.target.value);
                        if (sel) setCurrency(sel);
                      }}
                    >
                      {CURRENCY_OPTIONS.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.symbol} — {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="pricing-input-row">
                    <label className="pricing-label">Extraction ({currency.symbol}/page):</label>
                    <div className="pricing-input-wrap">
                      <span className="pricing-currency-symbol">{currency.symbol}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="pricing-input with-symbol"
                        value={extractionPrice}
                        onChange={(e) => setExtractionPrice(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>

                  <div className="pricing-input-row">
                    <label className="pricing-label">
                      Classification ({currency.symbol}/page):
                    </label>
                    <div className="pricing-input-wrap">
                      <span className="pricing-currency-symbol">{currency.symbol}</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className="pricing-input with-symbol"
                        value={classificationPrice}
                        onChange={(e) => setClassificationPrice(parseFloat(e.target.value) || 0)}
                      />
                    </div>
                  </div>
                </div>

                <div className="modal-estimate-total">
                  <span>Estimated Total:</span>
                  <strong>
                    {formatAmountWithCurrency(
                      genEstPages * extractionPrice + genEstPages * classificationPrice,
                      currency.symbol
                    )}
                  </strong>
                </div>
              </div>
            )}

            {genStartDate && genEndDate && genEstPages === 0 && (
              <div className="modal-no-usage" style={{ fontFamily }}>
                No unbilled pages found for this period.
              </div>
            )}

            <div className="modal-info-note" style={{ fontFamily }}>
              <AlertCircle size={14} />
              <span>
                Set price per page for each process type. Use 0 for free. Select your currency
                above.
              </span>
            </div>

            <div className="modal-buttons">
              <Button
                disableElevation
                variant="contained"
                className="review-btn-filled"
                style={{ fontFamily }}
                onClick={handleGenerateInvoice}
                disabled={
                  !genStartDate ||
                  !genEndDate ||
                  !toISODate(genStartDate) ||
                  !toISODate(genEndDate) ||
                  genEstPages === 0 ||
                  generating
                }
              >
                {generating ? "Generating…" : "Generate Invoice"}
              </Button>
              <Button
                disableElevation
                variant="contained"
                className="cancel-btn-filled"
                onClick={() => setIsGenerateModalOpen(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </Modal>
        <Modal open={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
          <div className="modal-container edit">
            <h6 className="modal-title" style={{ fontFamily }}>
              EDIT INVOICE
            </h6>
            <div className="modal-divider"></div>

            {editingInvoice && (
              <div className="modal-edit-form" style={{ fontFamily }}>
                <div className="edit-field-row">
                  <label className="edit-field-label">Invoice Number</label>
                  <input
                    type="text"
                    className="edit-field-input"
                    value={editFields.invoiceNumber || ""}
                    onChange={(e) =>
                      setEditFields({ ...editFields, invoiceNumber: e.target.value })
                    }
                  />
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Period Start</label>
                  <div className="edit-field-date">
                    <FormDate
                      label=""
                      name="periodStart"
                      value={toDisplayDate(editFields.periodStart || "")}
                      onChange={(e: any) =>
                        setEditFields({ ...editFields, periodStart: toISODate(e.target.value) })
                      }
                    />
                  </div>
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Period End</label>
                  <div className="edit-field-date">
                    <FormDate
                      label=""
                      name="periodEnd"
                      value={toDisplayDate(editFields.periodEnd || "")}
                      onChange={(e: any) =>
                        setEditFields({ ...editFields, periodEnd: toISODate(e.target.value) })
                      }
                    />
                  </div>
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Total Pages</label>
                  <input
                    type="number"
                    className="edit-field-input"
                    value={editFields.totalPages || 0}
                    onChange={(e) => {
                      const pages = parseInt(e.target.value) || 0;
                      setEditFields({
                        ...editFields,
                        totalPages: pages,
                        totalAmount: calculateAmount(pages),
                      });
                    }}
                  />
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Amount (£)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="edit-field-input"
                    value={editFields.totalAmount || 0}
                    onChange={(e) =>
                      setEditFields({ ...editFields, totalAmount: parseFloat(e.target.value) || 0 })
                    }
                  />
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Status</label>
                  <select
                    className="edit-field-input"
                    value={editFields.status || "pending"}
                    onChange={(e) => setEditFields({ ...editFields, status: e.target.value })}
                  >
                    <option value="paid">Paid</option>
                    <option value="pending">Pending</option>
                    <option value="overdue">Overdue</option>
                    <option value="draft">Draft</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Notes</label>
                  <input
                    type="text"
                    className="edit-field-input"
                    value={editFields.notes || ""}
                    onChange={(e) => setEditFields({ ...editFields, notes: e.target.value })}
                    placeholder="Optional notes"
                  />
                </div>

                <div className="edit-field-row">
                  <label className="edit-field-label">Email to</label>
                  <input
                    type="text"
                    className="edit-field-input"
                    value={emailIds}
                    onChange={(e) => setEmailIds(e.target.value)}
                    placeholder="email1@example.com, email2@example.com"
                  />
                </div>
              </div>
            )}

            <div className="modal-buttons">
              <Button
                disableElevation
                variant="contained"
                className="cancel-btn-filled"
                onClick={() => {
                  setIsEditModalOpen(false);
                  setEditingInvoice(null);
                }}
              >
                Cancel
              </Button>
              <Button
                disableElevation
                variant="contained"
                className="review-btn-filled"
                onClick={handleSaveEdit}
              >
                Save Changes
              </Button>
            </div>
          </div>
        </Modal>
        {/* ===== DELETE CONFIRMATION MODAL ===== */}
        <Modal open={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)}>
          <div className="modal-container delete">
            <h6 className="modal-title delete-title" style={{ fontFamily }}>
              DELETE INVOICE
            </h6>
            <div className="modal-divider delete-divider"></div>
            <div className="modal-delete-message" style={{ fontFamily }}>
              Are you sure you want to delete this invoice? The associated extraction page usage
              will be marked as unbilled.
            </div>
            <div className="modal-delete-message" style={{ fontFamily }}>
              <label htmlFor="delete-reason" className="modal-delete-input-label">
                Reason for deletion (required):
              </label>
              <input
                id="delete-reason"
                type="text"
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="modal-delete-input"
                placeholder="Enter reason…"
                required
              />
            </div>
            <div className="modal-buttons">
              <Button
                disableElevation
                variant="contained"
                className="cancel-btn-filled"
                onClick={() => {
                  setIsDeleteModalOpen(false);
                  setDeleteReason("");
                }}
              >
                Cancel
              </Button>
              <Button
                disableElevation
                variant="contained"
                className="modal-button danger"
                style={{ fontFamily }}
                onClick={handleDeleteInvoice}
                disabled={!deleteReason.trim()}
              >
                Delete Invoice
              </Button>
            </div>
          </div>
        </Modal>
        {/* ===== SNACKBAR ===== */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={5000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
      {/* ===== PAGINATION ===== */}
      <ConditionalPagination
        itemCount={filteredRows.length}
        itemsPerPage={rowsPerPage}
        threshold={rowsPerPage}
        paginationProps={{
          page: currentPage,
          onChange: handlePageChange,
          style: paginationstyle,
          hidePrevButton: false,
          hideNextButton: false,
          showFirstButton: true,
          showLastButton: true,
        }}
      />
    </>
  );
}
